/**
 * BESTIX v5.0 — EventDetailPage
 * Single event detail page with ticket listings - powered by Bespokian Events API
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEventDetail } from '@/queries/events.queries';
import { useCartStore } from '@/stores/cart.store';
import type { Event, Ticket } from '@/lib/api';

function formatPrice(dollars: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(dollars);
}

function formatDate(dateString: string, timeString: string): string {
  try {
    const date = new Date(dateString);
    const formatted = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return `${formatted} • ${timeString}`;
  } catch {
    return `${dateString} • ${timeString}`;
  }
}

interface TicketCardProps {
  ticket: Ticket;
  event: Event;
  onSelect: (ticket: Ticket, quantity: number) => void;
}

function TicketCard({ ticket, event, onSelect }: TicketCardProps) {
  const [quantity, setQuantity] = useState(ticket.purchasableQuantities[0] || 1);

  const handleSelect = () => {
    onSelect(ticket, quantity);
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-gold transition-colors">
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="font-semibold text-navy">
            {ticket.section} {ticket.row && `• Row ${ticket.row}`}
          </div>
          <div className="text-sm text-gray-500">
            {ticket.availableQuantity} tickets available
            {ticket.seats && ` • Seats ${ticket.seats.low}-${ticket.seats.high}`}
          </div>
        </div>
        <div className="text-right">
          <div className="font-bold text-navy">
            {formatPrice(ticket.price.wholesale)}
          </div>
          <div className="text-xs text-gray-500">each</div>
        </div>
      </div>
      
      {/* Features */}
      <div className="flex gap-2 flex-wrap mb-3">
        {ticket.isInstantDelivery && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
            Instant Delivery
          </span>
        )}
        {ticket.isETicket && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
            E-Ticket
          </span>
        )}
        {ticket.deliveryMethods.map((method) => (
          <span
            key={method}
            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
          >
            {method}
          </span>
        ))}
      </div>

      {ticket.notes && (
        <p className="text-xs text-amber-600 mb-3">{ticket.notes}</p>
      )}

      {/* Quantity and Buy */}
      <div className="flex items-center gap-3">
        <select
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          {ticket.purchasableQuantities.map((q) => (
            <option key={q} value={q}>
              {q} {q === 1 ? 'ticket' : 'tickets'}
            </option>
          ))}
        </select>
        <button
          onClick={handleSelect}
          className="flex-1 btn btn-primary text-center"
        >
          Add to Cart • {formatPrice(ticket.price.wholesale * quantity)}
        </button>
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const addToCart = useCartStore((state) => state.addItem);
  
  const eventId = id ? parseInt(id, 10) : null;
  const { data, isLoading, error } = useEventDetail(eventId);

  const handleSelectTicket = (ticket: Ticket, quantity: number) => {
    if (!data?.event) return;
    addToCart(data.event, ticket, quantity);
    navigate('/checkout');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold border-t-transparent" />
      </div>
    );
  }

  if (error || !data?.event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-navy mb-4">Event Not Found</h1>
          <p className="text-gray-600 mb-4">This event may no longer be available.</p>
          <button
            onClick={() => navigate('/events')}
            className="text-gold hover:underline"
          >
            Browse all events
          </button>
        </div>
      </div>
    );
  }

  const { event, tickets, totalTickets } = data;

  return (
    <>
      <Helmet>
        <title>{event.name} | BESTIX</title>
        <meta name="description" content={`Get tickets for ${event.name} at ${event.venue.name}`} />
      </Helmet>

      <div className="bg-gray-50 min-h-screen">
        {/* Hero */}
        <div className="relative h-[400px] bg-navy">
          {event.imageUrl && (
            <img
              src={event.imageUrl}
              alt={event.name}
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-navy to-transparent" />
          <div className="relative container mx-auto px-4 h-full flex items-end pb-8">
            <div>
              <div className="text-gold text-sm font-medium uppercase tracking-wider mb-2">
                {event.category}
              </div>
              <h1 className="font-playfair text-4xl md:text-5xl font-bold !text-white mb-4">
                {event.name}
              </h1>
              <p className="text-gray-300">
                {event.venue.name}
                {event.venue.city && ` · ${event.venue.city}`}
                {event.venue.state && `, ${event.venue.state}`}
              </p>
              <p className="text-white mt-2">
                {formatDate(event.date, event.time)}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h2 className="font-playfair text-2xl font-semibold text-navy mb-4">
                  About This Event
                </h2>
                
                {event.performers.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Performers</h3>
                    <div className="flex flex-wrap gap-2">
                      {event.performers.map((performer) => (
                        <span
                          key={performer.id}
                          className="px-3 py-1 bg-gray-100 rounded-full text-sm"
                        >
                          {performer.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-8">
                  <h3 className="font-semibold text-navy mb-4">Venue Information</h3>
                  <p className="text-gray-600">{event.venue.name}</p>
                  {(event.venue.city || event.venue.state) && (
                    <p className="text-gray-500 text-sm">
                      {[event.venue.city, event.venue.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>

                {event.priceRange && (
                  <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-navy mb-2">Price Range</h3>
                    <p className="text-2xl font-bold text-gold">
                      {formatPrice(event.priceRange.min)} - {formatPrice(event.priceRange.max)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar - Tickets */}
            <div>
              <div className="bg-white rounded-xl p-6 shadow-md sticky top-24">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-playfair text-xl font-semibold text-navy">
                    Available Tickets
                  </h2>
                  <span className="text-sm text-gray-500">
                    {totalTickets} listings
                  </span>
                </div>
                
                {tickets.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-2">No tickets currently available</p>
                    <p className="text-sm text-gray-400">
                      Check back later or try a different event
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {tickets.map((ticket) => (
                      <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        event={event}
                        onSelect={handleSelectTicket}
                      />
                    ))}
                  </div>
                )}

                <p className="mt-4 text-xs text-gray-500 text-center">
                  All purchases protected by our Buyer Guarantee
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
